<template>
  <div class="BaseCheckBox">
    <div class="row">
      <input
        type="checkbox"
        :id="id"
        v-model="value"
        color="primary"
        :hide-details="true"
        :disabled="disabled"
        @keydown.enter.prevent="!disabled && (value = !value)"
      />
      <div>
        <label
          :for="id"
          v-html="label"
          :class="{ 'is-disabled': disabled }"
        />

        <p
          class="description"
          v-if="description"
        >
          {{ description }}
        </p>
      </div>
    </div>

    <div
      ref="hiddenFieldWrapper"
      style="display: none"
      aria-hidden="true"
    >
      <v-text-field
        :id="`${id}-invisible`"
        :key="`${id}-invisible`"
        ref="$field"
        aria-hidden="true"
        :aria-describedby="undefined"
        tabindex="-1"
        v-model="isValid"
        :rules="rules"
        :hide-details="true"
      />
    </div>

    <ErrorMessageComponent
      v-if="rules.length"
      :messages="fieldValidationErrorMessage"
    />
  </div>
</template>

<script setup lang="ts">
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '@/modules/core/composables/accesibilityComposable'
  import { useValidationError } from '@/modules/core/composables/validationErrorsComposable'
  import type { ValidationRuleType } from '@/modules/core/models/types'
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  import ErrorMessageComponent from '../formFields/ErrorMessageComponent.vue'

  interface PropsInterface {
    id: string
    modelValue: boolean
    label: string
    description?: string
    rules?: ValidationRuleType[]
    disabled?: boolean
  }

  withDefaults(defineProps<PropsInterface>(), {
    rules: () => []
  })

  const isValid = computed(() => value.value || '')

  const value = defineModel()

  const { $field, fieldValidationErrorMessage } = useValidationError()

  const hiddenFieldWrapper = ref<HTMLElement | null>(null)
  let hiddenObserver: MutationObserver | null = null

  onMounted(() => {
    hiddenObserver = startDuplicatedLabelControl(hiddenFieldWrapper.value, {
      labelOrphanedInputs: true
    })
  })
  onUnmounted(() => endDuplicatedLabelControl(hiddenObserver))
</script>

<style lang="scss" scoped>
  .BaseCheckBox {
    .row {
      display: flex;
      gap: $gap-m;
      align-items: start;

      [type='checkbox'] {
        min-width: $size-20;
        height: $size-20;
        margin-top: $spacing-2;
        accent-color: $color-primary;
        box-shadow: $box-shadow-0;
        clip-path: circle(100%);
        cursor: pointer;

        &:focus-visible {
          clip-path: none;
          box-shadow: 0 0 0 4px $color-focus;
        }

        &:hover {
          box-shadow: 0 0 0 10px hsl(219deg 100% 69% / 10%);

          &:checked {
            animation: pulse 0.8s ease-in-out;
          }
        }
      }

      label {
        color: $color-neutral-dark;
        font-weight: $font-weight-400;
        line-height: $line-height-140p;
      }

      .description {
        color: $color-neutral;
      }

      .is-disabled {
        color: $color-grey-400;
      }
    }
  }
</style>
